import React, { useState, useEffect } from "react";
import AdminService from "../services/AdminService";
import { toast } from "react-toastify";

const SubscriptionManagement = () => {
    // Shared State
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("subscriptions");
    const [searchTerm, setSearchTerm] = useState("");

    // Subscriptions Tab State
    const [subscriptions, setSubscriptions] = useState([]);
    const [filter, setFilter] = useState("all");

    // Plans Tab State
    const [planDTOs, setPlanDTOs] = useState([]);
    const [showPlanModal, setShowPlanModal] = useState(false);
    const [isEditingPlan, setIsEditingPlan] = useState(false);
    const [isSavingPlan, setIsSavingPlan] = useState(false);
    const [currentPlan, setCurrentPlan] = useState({
        id: "", type: "", description: "", price: "", validity: "", adsLimit: "", discount: 0, status: true
    });

    // Top-Up Tab State
    const [topUpPackages, setTopUpPackages] = useState([]);
    const [showPackageModal, setShowPackageModal] = useState(false);
    const [packageForm, setPackageForm] = useState({ id: "", name: "", price: "", coins: "" });

    useEffect(() => {
        loadData();
    }, [activeTab]);

    const loadData = () => {
        if (activeTab === "subscriptions") loadSubscriptions();
        else if (activeTab === "plans") loadPlans();
        else if (activeTab === "packages") loadTopUpPackages();
    };

    const loadSubscriptions = async () => {
        setLoading(true);
        try {
            const res = await AdminService.getUserSubscriptions();
            if (res.data?.status === true || res.status === 200) {
                setSubscriptions(res.data.data || []);
            }
        } catch (error) {
            console.error("Failed to load user subscriptions:", error);
            toast.error("Failed to load user subscriptions");
        } finally {
            setLoading(false);
        }
    };

    const loadPlans = async () => {
        setLoading(true);
        try {
            const res = await AdminService.getPlans();
            console.log("Plans API Response:", res.data);
            if (res.data?.status === true || res.status === 200) {
                // Now receiving SubscriptionResponseDTO: { subscription: {}, activeSubscribersCount: N }
                setPlanDTOs(res.data.data || []);
            }
        } catch (error) {
            console.error("Failed to load plans:", error);
            toast.error("Failed to load plans");
        } finally {
            setLoading(false);
        }
    };

    const loadTopUpPackages = async () => {
        setLoading(true);
        try {
            const res = await AdminService.getTopUpPackages();
            if (res.data?.content || res.status === 200) {
                setTopUpPackages(res.data.content || []);
            }
        } catch (error) {
            console.error("Failed to load top-up packages:", error);
            toast.error("Failed to load top-up packages");
        } finally {
            setLoading(false);
        }
    };

    const isExpiringSoon = (expiryDate) => {
        if (!expiryDate) return false;
        const expiry = new Date(expiryDate);
        const now = new Date();
        const diff = (expiry - now) / (1000 * 60 * 60 * 24);
        return diff > 0 && diff <= 7;
    };

    // Plan Actions
    const handleEditPlan = (plan) => {
        setIsEditingPlan(true);
        setCurrentPlan(plan);
        setShowPlanModal(true);
    };

    const handleDeletePlan = async (dto) => {
        if (!dto) return;

        // Smarter data extraction: handle both DTO wrapper and raw Subscription object
        const subscription = dto.subscription || (dto.id ? dto : null);
        const activeSubscribersCount = dto.activeSubscribersCount !== undefined ? dto.activeSubscribersCount : 0;

        // If we found active subscribers, block deletion with the user's requested message
        if (activeSubscribersCount > 0) {
            toast.error(`You cannot delete this plan because there are active user subscriptions associated with it.`);
            return;
        }

        if (!subscription) {
            toast.error("Plan data not available for deletion.");
            return;
        }

        // Final confirmation for plans with 0 active users
        if (!window.confirm(`Are you sure you want to delete the "${subscription.type || 'selected'}" plan?`)) return;

        try {
            await AdminService.deletePlan(subscription.id);
            toast.success("Plan deleted successfully");
            loadPlans();
        } catch (error) {
            const errorMsg = error.response?.data?.message || "Failed to delete plan";
            toast.error(errorMsg);
        }
    };

    const handleTogglePlanStatus = async (id, currentStatus) => {
        try {
            await AdminService.togglePlanStatus(id, !currentStatus);
            toast.success(`Plan ${!currentStatus ? 'activated' : 'deactivated'}`);
            loadPlans();
        } catch (error) {
            toast.error("Failed to change status");
        }
    };

    const handlePlanSubmit = async (e) => {
        e.preventDefault();
        if (isSavingPlan) return;
        setIsSavingPlan(true);
        try {
            if (isEditingPlan) {
                await AdminService.updatePlanDetails(currentPlan);
                toast.success("Plan updated");
            } else {
                await AdminService.createPlan(currentPlan);
                toast.success("Plan created");
            }
            setShowPlanModal(false);
            loadPlans();
        } catch (error) {
            toast.error("Failed to save plan");
        } finally {
            setIsSavingPlan(false);
        }
    };

    // Package Actions
    const handleSavePackage = async () => {
        try {
            await AdminService.updateTopUpPackage(packageForm);
            toast.success("Package updated successfully");
            setShowPackageModal(false);
            loadTopUpPackages();
        } catch (error) {
            toast.error("Failed to update package");
        }
    };

    const filteredSubs = subscriptions.filter(sub => {
        const matchesFilter = filter === "all" || sub.status?.toLowerCase() === filter.toLowerCase();
        const matchesSearch =
            (sub.userName || sub.user)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (sub.planName || sub.plan)?.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesFilter && matchesSearch;
    });

    return (
        <div>
            <div className="admin-tabs">
                <button className={`admin-tab ${activeTab === "subscriptions" ? "active" : ""}`} onClick={() => setActiveTab("subscriptions")}>
                    User Subscriptions
                </button>
                <button className={`admin-tab ${activeTab === "plans" ? "active" : ""}`} onClick={() => setActiveTab("plans")}>
                    Subscription Plans
                </button>
                <button className={`admin-tab ${activeTab === "packages" ? "active" : ""}`} onClick={() => setActiveTab("packages")}>
                    Top-Up Packages
                </button>
            </div>

            {/* User Subscriptions Tab */}
            {activeTab === "subscriptions" && (
                <>
                    <div className="admin-card">
                        <div className="admin-card-body">
                            <div className="admin-filters">
                                <input
                                    type="text"
                                    className="admin-search-input"
                                    placeholder="Search by user or plan..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <select className="admin-filter-select" value={filter} onChange={(e) => setFilter(e.target.value)}>
                                    <option value="all">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="expired">Expired</option>
                                    <option value="canceled">Canceled</option>
                                </select>
                                <span style={{ fontSize: "13px", color: "#6c757d" }}>{filteredSubs.length} result(s)</span>
                            </div>
                        </div>
                    </div>

                    <div className="admin-card">
                        <div className="admin-card-header">
                            <h3>Active User Subscriptions</h3>
                        </div>
                        <div className="admin-card-body" style={{ padding: 0 }}>
                            {loading ? (
                                <div style={{ textAlign: "center", padding: "40px" }}><i className="fa fa-spinner fa-spin fa-2x"></i></div>
                            ) : (
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>User</th>
                                            <th>Purchased Plan</th>
                                            <th>Price Paid</th>
                                            <th>Start Date</th>
                                            <th>Expiry Date</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredSubs.map((sub, index) => (
                                            <tr key={sub.subscriptionId || index}>
                                                <td style={{ fontWeight: 500 }}>{sub.userName || sub.user}</td>
                                                <td>{sub.planName || sub.plan}</td>
                                                <td>${sub.amount !== undefined ? sub.amount : (sub.pricePaid || 0)}</td>
                                                <td>{sub.startDate ? new Date(sub.startDate).toLocaleDateString() : 'N/A'}</td>
                                                <td>
                                                    {sub.expiryDate ? new Date(sub.expiryDate).toLocaleDateString() : 'N/A'}
                                                    {isExpiringSoon(sub.expiryDate) && (
                                                        <span style={{ color: "#dc3545", fontSize: "11px", marginLeft: "6px" }}>
                                                            <i className="fa fa-exclamation-triangle"></i> Expiring soon
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className={`admin-badge ${sub.status?.toLowerCase() === 'active' ? 'active' : 'failed'}`}>
                                                        {sub.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                        {filteredSubs.length === 0 && (
                                            <tr>
                                                <td colSpan="6" style={{ textAlign: "center", padding: "40px", color: "#6c757d" }}>
                                                    No user subscriptions found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* Subscription Plans Tab */}
            {activeTab === "plans" && (
                <div className="admin-card">
                    <div className="admin-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3>Manage Subscription Plans</h3>
                        <button className="admin-btn-primary admin-btn" onClick={() => { setIsEditingPlan(false); setCurrentPlan({ id: "", type: "", description: "", price: "", validity: "", adsLimit: "", discount: 0, status: true }); setShowPlanModal(true); }}>
                            <i className="fa fa-plus"></i> Create New Plan
                        </button>
                    </div>
                    <div className="admin-card-body" style={{ padding: 0 }}>
                        {loading ? (
                            <div style={{ textAlign: "center", padding: "40px" }}><i className="fa fa-spinner fa-spin fa-2x"></i></div>
                        ) : (
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Plan Name</th>
                                        <th>Price</th>
                                        <th>Duration</th>
                                        <th>Ad Limits</th>
                                        <th>Users</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {planDTOs.map((dto) => {
                                        // Handle both nested { subscription, activeSubscribersCount } and flat { id, type, ... }
                                        const plan = dto?.subscription || (dto?.type ? dto : null);
                                        const subscribers = dto?.activeSubscribersCount || 0;

                                        if (!plan) return null;

                                        return (
                                            <tr key={plan.id || Math.random()}>
                                                <td style={{ fontWeight: 500 }}>
                                                    <div style={{ display: "flex", flexDirection: "column" }}>
                                                        <span>{plan.type || "Unknown Plan"}</span>
                                                        <small style={{ color: "#6c757d", fontSize: "11px" }}>{plan.description || "No description"}</small>
                                                    </div>
                                                </td>
                                                <td>${plan.price?.toLocaleString() || 0}</td>
                                                <td>{plan.validity || 0} days</td>
                                                <td>{plan.adsLimit || 0} ads</td>
                                                <td>
                                                    <span style={{ fontWeight: 600, color: subscribers > 0 ? "var(--admin-primary)" : "#6c757d" }}>
                                                        {subscribers} active
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`admin-badge ${plan.status ? "active" : "failed"}`}>
                                                        {plan.status ? "Active" : "Inactive"}
                                                    </span>
                                                </td>
                                                <td style={{ display: "flex", gap: "5px", alignItems: "center" }}>
                                                    <button className="admin-btn-icon" title="Edit Plan" onClick={() => handleEditPlan(plan)}>
                                                        <i className="fa fa-edit"></i>
                                                    </button>
                                                    <button className="admin-btn-icon" title={plan.status ? "Deactivate Plan" : "Activate Plan"} onClick={() => handleTogglePlanStatus(plan.id, plan.status)}>
                                                        <i className={`fa ${plan.status ? "fa-toggle-on" : "fa-toggle-off"}`} style={{ color: plan.status ? "var(--admin-success)" : "var(--admin-danger)" }}></i>
                                                    </button>
                                                    <button className="admin-btn-icon" title="Delete Plan" onClick={() => handleDeletePlan(dto)}>
                                                        <i className="fa fa-trash" style={{ color: subscribers > 0 ? "#ccc" : "var(--admin-danger)" }}></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {/* Top-Up Packages Tab */}
            {activeTab === "packages" && (
                <div className="admin-card">
                    <div className="admin-card-header">
                        <h3>Top-Up Coins Packages</h3>
                    </div>
                    <div className="admin-card-body" style={{ padding: 0 }}>
                        {loading ? (
                            <div style={{ textAlign: "center", padding: "40px" }}><i className="fa fa-spinner fa-spin fa-2x"></i></div>
                        ) : (
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Package Name</th>
                                        <th>Coins</th>
                                        <th>Price</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {topUpPackages.map((pkg) => (
                                        <tr key={pkg.id}>
                                            <td style={{ fontWeight: 500 }}>{pkg.name || "Top-up"}</td>
                                            <td>{pkg.coins} coins</td>
                                            <td>${pkg.cost || pkg.price}</td>
                                            <td>
                                                <button className="admin-btn-icon" onClick={() => { setPackageForm(pkg); setShowPackageModal(true); }}><i className="fa fa-pencil"></i></button>
                                            </td>
                                        </tr>
                                    ))}
                                    {topUpPackages.length === 0 && (
                                        <tr>
                                            <td colSpan="4" style={{ textAlign: "center", padding: "40px", color: "#6c757d" }}>
                                                No packages found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {/* Modal for Plan */}
            {showPlanModal && (
                <div className="admin-modal-overlay" onClick={() => setShowPlanModal(false)}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h3>{isEditingPlan ? "Edit Plan" : "Create New Plan"}</h3>
                        </div>
                        <div className="admin-modal-body">
                            <form onSubmit={handlePlanSubmit}>
                                <div className="admin-form-group">
                                    <label>Plan Name</label>
                                    <input type="text" className="admin-form-control" value={currentPlan.type} onChange={(e) => setCurrentPlan({ ...currentPlan, type: e.target.value })} required />
                                </div>
                                <div className="admin-form-row">
                                    <div className="admin-form-group">
                                        <label>Price ($)</label>
                                        <input type="number" className="admin-form-control" value={currentPlan.price} onChange={(e) => setCurrentPlan({ ...currentPlan, price: e.target.value })} required />
                                    </div>
                                    <div className="admin-form-group">
                                        <label>Validity (Days)</label>
                                        <input type="number" className="admin-form-control" value={currentPlan.validity} onChange={(e) => setCurrentPlan({ ...currentPlan, validity: e.target.value })} required />
                                    </div>
                                </div>
                                <div className="admin-form-group">
                                    <label>Ad Limit</label>
                                    <input type="number" className="admin-form-control" value={currentPlan.adsLimit} onChange={(e) => setCurrentPlan({ ...currentPlan, adsLimit: e.target.value })} required />
                                </div>
                                <div className="admin-modal-footer">
                                    <button type="button" className="admin-btn admin-btn-outline" onClick={() => setShowPlanModal(false)} disabled={isSavingPlan}>Cancel</button>
                                    <button type="submit" className="admin-btn admin-btn-primary" disabled={isSavingPlan}>
                                        {isSavingPlan ? (
                                            <><i className="fa fa-spinner fa-spin"></i> Saving...</>
                                        ) : "Save Plan"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal for Top-up Package */}
            {showPackageModal && (
                <div className="admin-modal-overlay" onClick={() => setShowPackageModal(false)}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal-header"><h3>Edit Package</h3></div>
                        <div className="admin-modal-body">
                            <div className="admin-form-group">
                                <label>Package Name</label>
                                <input type="text" className="admin-form-control" value={packageForm.name} onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })} />
                            </div>
                            <div className="admin-form-row">
                                <div className="admin-form-group">
                                    <label>Coins</label>
                                    <input type="number" className="admin-form-control" value={packageForm.coins} onChange={(e) => setPackageForm({ ...packageForm, coins: e.target.value })} />
                                </div>
                                <div className="admin-form-group">
                                    <label>Price ($)</label>
                                    <input type="number" className="admin-form-control" value={packageForm.cost || packageForm.price} onChange={(e) => setPackageForm({ ...packageForm, cost: e.target.value, price: e.target.value })} />
                                </div>
                            </div>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="admin-btn admin-btn-outline" onClick={() => setShowPackageModal(false)}>Cancel</button>
                            <button className="admin-btn admin-btn-primary" onClick={handleSavePackage}>Update Package</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SubscriptionManagement;
